import { createFileRoute } from '@tanstack/react-router';
import { Check, X } from "lucide-react";
import { ContentHeader, ContentSection, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/comparisons/kong')({
  component: KongComparisonPage,
  head: () => ({
    meta: [
      { title: 'Matricks vs Kong | Matricks' },
      { name: 'description', content: 'Compare Matricks and Kong for API gateway and management. See differences in features, pricing, and approach.' },
    ],
  }),
});

const comparisonData = [
  { feature: "API Gateway", matricks: true, kong: true },
  { feature: "Rate Limiting", matricks: true, kong: true },
  { feature: "Authentication (OAuth, API Keys)", matricks: true, kong: true },
  { feature: "Pre-built Integrations", matricks: "2000+", kong: "Limited" },
  { feature: "Visual Workflow Builder", matricks: true, kong: false },
  { feature: "AI/LLM Gateway", matricks: true, kong: false },
  { feature: "AI Agent Platform", matricks: true, kong: false },
  { feature: "Fine-Grained Policies (ABAC)", matricks: true, kong: "Plugin" },
  { feature: "Field-Level Access Control", matricks: true, kong: false },
  { feature: "Cost Tracking (AI/API)", matricks: true, kong: false },
  { feature: "No-Code Setup", matricks: true, kong: false },
  { feature: "Self-Hosted Option", matricks: true, kong: true },
  { feature: "Cloud-Native", matricks: true, kong: true },
];

const faqItems = [
  {
    question: "Can I migrate from Kong to Matricks?",
    answer: "Yes, Matricks can run alongside Kong during migration. We support gradual traffic shifting so you can validate the new setup before fully migrating. Our team can assist with migration planning.",
  },
  {
    question: "Does Matricks support Kong plugins?",
    answer: "Matricks has its own plugin/extension system that covers similar use cases. While Kong plugins aren't directly compatible, most functionality has native equivalents or can be implemented with custom policies.",
  },
  {
    question: "How does pricing compare?",
    answer: "Kong Enterprise pricing is typically based on API calls and features. Matricks offers transparent pricing with all features included. For most organizations, Matricks is more cost-effective, especially with the included AI capabilities.",
  },
];

function KongComparisonPage() {
  return (
    <div>
      <ContentHeader
        title="Matricks vs Kong"
        description="Kong is a popular open-source API gateway. Here's how Matricks compares for teams looking for more than just API routing."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Comparisons", href: "/comparisons" },
          { label: "vs Kong" },
        ]}
      />

      {/* Overview */}
      <ContentSection id="overview" title="Overview">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg">Kong</h3>
            <p className="text-muted-foreground mt-2">
              Kong is an open-source API gateway built on NGINX. It excels at high-performance
              API routing and offers an ecosystem of plugins for extending functionality. Best
              suited for teams with DevOps expertise who need low-level control.
            </p>
          </div>
          <div className="rounded-xl border bg-primary/5 border-primary/20 p-6">
            <h3 className="font-semibold text-lg">Matricks</h3>
            <p className="text-muted-foreground mt-2">
              Matricks is an enterprise integration platform that combines API gateway capabilities
              with AI agent orchestration, 2000+ pre-built integrations, and fine-grained access
              control. Best for teams wanting a complete solution without extensive custom development.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Comparison Table */}
      <ContentSection
        id="comparison"
        title="Feature Comparison"
        background="muted"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="py-4 px-4 text-left font-semibold">Feature</th>
                <th className="py-4 px-4 text-center font-semibold bg-primary/5 text-primary">Matricks</th>
                <th className="py-4 px-4 text-center font-semibold">Kong</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row) => (
                <tr key={row.feature} className="border-b last:border-0">
                  <td className="py-4 px-4">{row.feature}</td>
                  <td className="py-4 px-4 text-center bg-primary/5">
                    {row.matricks === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.matricks === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm">{row.matricks}</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {row.kong === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.kong === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm text-muted-foreground">{row.kong}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      {/* Key Differences */}
      <ContentSection id="differences" title="Key Differences">
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Integration Approach</h3>
            <p className="text-muted-foreground mt-2">
              Kong requires custom development for most integrations. Matricks provides 2000+
              pre-built connectors with one-click setup. This dramatically reduces time-to-value
              for common integration scenarios.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">AI Capabilities</h3>
            <p className="text-muted-foreground mt-2">
              Matricks includes a full AI/LLM gateway and agent platform. Kong has no native AI
              support—you'd need to build and integrate this separately. For AI-powered applications,
              Matricks provides a complete solution.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Operational Overhead</h3>
            <p className="text-muted-foreground mt-2">
              Kong requires significant DevOps expertise to deploy, configure, and maintain.
              Matricks is fully managed with a no-code interface for most tasks. Teams can be
              productive immediately without extensive training.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* When to Choose */}
      <ContentSection
        id="when-to-choose"
        title="When to Choose Each"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold mb-4">Choose Kong When:</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li>• You have strong DevOps expertise in-house</li>
              <li>• You need maximum customization at the infrastructure level</li>
              <li>• You're already invested in the Kong ecosystem</li>
              <li>• Your use case is pure API routing without AI</li>
            </ul>
          </div>
          <div className="rounded-xl border bg-primary/5 border-primary/20 p-6">
            <h3 className="font-semibold mb-4">Choose Matricks When:</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li>• You want pre-built integrations instead of custom development</li>
              <li>• You need AI/LLM capabilities alongside API management</li>
              <li>• You prefer no-code/low-code configuration</li>
              <li>• You need fine-grained access control (field-level)</li>
              <li>• You want to minimize operational overhead</li>
            </ul>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Ready to Compare?"
        description="Try Matricks free for 14 days and see the difference for yourself."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "Contact Sales", href: "/info/contact" }}
        background="primary"
      />
    </div>
  );
}
